export interface FrontendLogEvent {
  level?: string;
  category?: string;
  eventName?: string;
  componentOrWidget?: string;
  path?: string;
  url?: string;
  sessionId?: string;
  requestId?: string;
  data?: unknown;
}

interface FrontendLoggerApi {
  logEvent: (event: FrontendLogEvent) => void;
  logError: (event: FrontendLogEvent) => void;
  flush?: () => Promise<void> | void;
}

function getFrontendLogger(): FrontendLoggerApi | null {
  if (typeof globalThis === "undefined") {
    return null;
  }

  const runtime = globalThis as typeof globalThis & {
    VgrFrontend?: {
      logger?: FrontendLoggerApi;
    };
  };

  return runtime.VgrFrontend?.logger ?? null;
}

export function logEvent(event: FrontendLogEvent) {
  getFrontendLogger()?.logEvent(event);
}

export function logError(event: FrontendLogEvent) {
  getFrontendLogger()?.logError(event);
}

const maxLoggedResponsePreviewLength = 200;

function getHeaderValue(headers: Headers, headerName: string) {
  const directMatch = headers.get(headerName);

  if (directMatch) {
    return directMatch;
  }

  const normalizedHeaderName = headerName.toLowerCase();

  for (const [name, value] of headers.entries()) {
    if (name.toLowerCase() === normalizedHeaderName) {
      return value;
    }
  }

  return undefined;
}

export function getSupportId(response: Response) {
  return getHeaderValue(response.headers, "SupportId") ?? undefined;
}

export function summarizeResponseText(
  responseText: string,
  options?: {
    contentType?: string;
    supportId?: string;
    maxPreviewLength?: number;
  }
) {
  const trimmedText = responseText.trim();
  const responseLength = trimmedText.length;
  const responseWasHtml =
    options?.contentType?.toLowerCase().includes("text/html") ?? false;

  if (responseWasHtml) {
    return {
      responseLength,
      responseWasHtml: true,
      supportId: options?.supportId,
    };
  }

  return {
    responseLength,
    responsePreview: trimmedText
      .replace(/\s+/g, " ")
      .slice(0, options?.maxPreviewLength ?? maxLoggedResponsePreviewLength),
    responseWasHtml: false,
    supportId: options?.supportId,
  };
}

export function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return undefined;
}

export async function getCompactResponseDetails(response: Response) {
  const contentType = response.headers.get("content-type") ?? undefined;
  const supportId = getSupportId(response);

  try {
    const responseText = await response.text();

    return {
      contentType,
      ...summarizeResponseText(responseText, {
        contentType,
        supportId,
        maxPreviewLength: maxLoggedResponsePreviewLength,
      }),
    };
  } catch {
    return {
      contentType,
      supportId,
    };
  }
}

export async function flushLogs() {
  await getFrontendLogger()?.flush?.();
}